import {
  Button,
  ChevronDownIcon,
  FlagIcon,
  IconButton,
  InfoIcon,
  Switch,
  Typography,
} from '@eventbrite/marmalade';
import { useEffect, useState } from 'react';
import styles from './RealityWarper.module.scss';
import { FlagState } from './types';

const Reality = ({
  flag,
  updateFlagState,
}: {
  flag: { name: string; value: boolean };
  updateFlagState: (state: Partial<FlagState>) => void;
}) => {
  const handleCheckedChange = (checked: boolean) => {
    updateFlagState({ [flag.name]: checked });
  };

  return (
    <div className={styles.flagContainer}>
      <Typography>{flag.value ? <b>{flag.name}</b> : flag.name}</Typography>
      <Switch
        aria-label={`flag for ${flag.name}`}
        onCheckedChange={handleCheckedChange}
        checked={flag.value}
        placeholder={flag.name}
      />
    </div>
  );
};

const RealityController = ({
  flagState,
  updateFlagState,
  dismissController,
}: {
  flagState: FlagState;
  updateFlagState: (state: Partial<FlagState>) => void;
  dismissController: () => void;
}) => {
  const options = Object.keys(flagState)
    .map((flag) => ({
      name: flag,
      value: Boolean(flagState[flag]),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className={styles.container}>
      <div className={styles.mainContainer}>
        <div className={styles.controllerHeader}>
          <Typography variant="body-lg">Flags</Typography>
          <IconButton
            aria-label={'reality-warper-controller-dismiss'}
            icon={<ChevronDownIcon />}
            onClick={() => dismissController()}
          />
        </div>

        <div className={styles.flagsContainer}>
          {options.map((flag, i) => (
            <Reality key={i} flag={flag} updateFlagState={updateFlagState} />
          ))}
        </div>

        <div className={styles.infoContainer}>
          <InfoIcon />
          <Typography variant="body-sm">This tool is only available in qa</Typography>
        </div>
      </div>
    </div>
  );
};

export const RealityWarper = ({
  flagState,
  updateFlagState,
}: {
  flagState: FlagState;
  updateFlagState: (state: Partial<FlagState>) => void;
}) => {
  const [showController, setShowController] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey && event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        setShowController((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (
    typeof window !== 'undefined' &&
    !window.location.hostname.includes('evbqa.com') &&
    !window.location.hostname.includes('localhost')
  )
    return null;

  return (
    <div
      className={`${styles.warperPanel} ${showController ? styles.active : ''}`}
      data-testid="reality-warper-panel"
    >
      <Button onClick={() => setShowController((prev) => !prev)}>
        <FlagIcon />
      </Button>

      {showController && (
        <RealityController
          flagState={flagState}
          updateFlagState={updateFlagState}
          dismissController={() => setShowController(false)}
        />
      )}
    </div>
  );
};
